/**
 * TallyPrime as the receivables source: setting it up, step by step, and the
 * connection once it is the source (docs/tally/09). Feature branch only.
 *
 * Which step shows is decided by the connection's own state in the database,
 * re-read every few seconds while it is being set up — the connector moves it
 * on (paired, companies found, company read) and this page follows.
 */
import { useCallback, useEffect, useState } from 'react';
import { Badge, Card, Button, ConfirmDialog } from '@timely/ui';
import type { SwitchExisting } from '@timely/domain';
import { FormNotice } from '../../screens/AuthLayout';
import { activateConnector, loadAccountsForSwitch, revokeConnector, type ConnectorStatus } from '../useConnector';
import { ConnectionPanel, TroubleshootingAndSecurity, useNow } from './ConnectionPanel';
import { SwitchReview } from './SwitchReview';
import { CompanyStep, PairingStep, PreviewStep, ReadingStep, StepList, TallyIntro } from './TallySetup';
import { ago } from './tallyCopy';
import { useFormatters } from '../../book/useFormatters';
import { ReceivablesOverview } from '../../receivables/ReceivablesOverview';
import { useActiveWorkspace } from '../../workspace/WorkspaceProvider';
import { holds } from '@timely/domain';

export function TallyDataSource({
    workspaceId, status, sheetConnected, canManage, canSync, reload, onWaitingForPairing,
}: {
    workspaceId: string;
    status: ConnectorStatus | null;
    sheetConnected: boolean;
    canManage: boolean;
    canSync: boolean;
    reload: () => Promise<void>;
    /** While a pairing code is waiting, the page re-reads the connection often. */
    onWaitingForPairing: (waiting: boolean) => void;
}) {
    const { date } = useFormatters();
    const context = useActiveWorkspace();
    const now = useNow();
    const live = status && status.status !== 'revoked' ? status : null;
    const [started, setStarted] = useState(false);
    const [choosingAgain, setChoosingAgain] = useState(false);
    const [reviewing, setReviewing] = useState(false);
    const [existing, setExisting] = useState<SwitchExisting[] | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // The book's accounts, once there is a preview to compare them with (owners only see the account list).
    const previewReady = Boolean(live?.status === 'pending' && live.preview);
    useEffect(() => {
        if (!previewReady || !canManage || existing !== null) return;
        loadAccountsForSwitch(workspaceId).then(setExisting, () => setExisting([]));
    }, [previewReady, canManage, existing, workspaceId]);
    const openAccounts = (existing ?? []).filter(a => Math.abs(a.total) > 0.005).length;
    const needsReview = sheetConnected || openAccounts > 0;

    // A setup that was never finished (the wrong computer paired, a reinstall): cancelled here, so a new pairing code
    // can be made. Before 4 Nov 2026 only an active connection could be disconnected, and an unfinished one blocked
    // every new code until it was fixed by hand.
    const [cancelling, setCancelling] = useState(false);
    const cancelSetup = useCallback(async () => {
        if (!live || busy) return;
        setBusy(true);
        setError(null);
        try {
            await revokeConnector(workspaceId, live.id);
            setStarted(false);
            setChoosingAgain(false);
            await reload();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'The setup could not be cancelled just now.');
        } finally { setBusy(false); }
    }, [live, busy, workspaceId, reload]);

    const connect = useCallback(async (bindings: { account: string; sourceKey: string }[], settle: string[]) => {
        if (!live?.sourceCompany || busy) return;
        setBusy(true);
        setError(null);
        try {
            await activateConnector(workspaceId, live.id, live.sourceCompany.id, bindings, settle, sheetConnected);
            setReviewing(false);
            await reload();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'TallyPrime could not be connected just now.');
        } finally { setBusy(false); }
    }, [live, busy, workspaceId, sheetConnected, reload]);

    // The active connection: the panel.
    if (live?.status === 'active') {
        return (
            <>
                <ConnectionPanel workspaceId={workspaceId} status={live} canManage={canManage} canSync={canSync}
                    onChanged={reload} onReplaced={() => { setStarted(true); void reload(); }} />
                <ReceivablesOverview workspaceId={workspaceId} canManage={canManage} seesWholeBook={holds(context, 'customers.view.all')} />
            </>
        );
    }

    // Being set up: which step the connection is at.
    let step: number;
    let body: React.ReactNode;
    if (!live) {
        if (!started) {
            step = 0;
            body = <TallyIntro sheetConnected={sheetConnected} canManage={canManage} onStart={() => setStarted(true)} />;
        } else {
            step = 2;
            body = <PairingStep workspaceId={workspaceId} onWaiting={onWaitingForPairing} />;
        }
    } else if (!live.sourceCompany || choosingAgain) {
        step = 3;
        body = <CompanyStep workspaceId={workspaceId} status={live} onChosen={() => { setChoosingAgain(false); setExisting(null); void reload(); }} />;
    } else if (!live.preview) {
        step = 4;
        body = <ReadingStep status={live} />;
    } else if (reviewing && existing) {
        step = 4;
        body = <SwitchReview status={live} existing={existing} sheetConnected={sheetConnected} busy={busy}
            onConnect={(b, s) => void connect(b, s)} onBack={() => setReviewing(false)} />;
    } else {
        step = 4;
        body = <PreviewStep workspaceId={workspaceId} canManage={canManage} status={live} needsReview={needsReview} busy={busy || (canManage && existing === null)}
            onChooseAgain={() => setChoosingAgain(true)}
            onConfirm={() => { if (needsReview) setReviewing(true); else void connect([], []); }} />;
    }

    return (
        <>
            {status?.status === 'revoked' && !started && (
                <Card className="p-5 mb-5">
                    <div className="flex items-center gap-2"><h3 className="text-heading font-semibold text-label">TallyPrime</h3><Badge>Disconnected</Badge></div>
                    <p className="mt-1 text-body text-label-2">
                        {status.sourceCompany ? `“${status.sourceCompany.name}” was disconnected` : 'The connector was disconnected'} {ago(status.revokedAt, now, date)}.
                        Your accounts keep their last figures from TallyPrime and all of their history; nothing updates them until a source is connected again.
                    </p>
                    {canManage && <div className="mt-4"><Button variant="primary" onClick={() => setStarted(true)}>Connect TallyPrime again</Button></div>}
                </Card>
            )}
            {(started || live) && <StepList current={step} />}
            {error && <FormNotice tone="error">{error}</FormNotice>}
            {(status?.status !== 'revoked' || started) && body}
            {live && canManage && (
                <div className="mt-5 flex items-center gap-3 flex-wrap">
                    <Button variant="quiet" size="sm" disabled={busy} onClick={() => setCancelling(true)}>Cancel this setup</Button>
                    <span className="text-secondary text-label-3">Paired the wrong computer, or starting again? Cancel it and make a new pairing code.</span>
                </div>
            )}
            {(started || live) && <TroubleshootingAndSecurity status={live} />}
            {live && (
                <ConfirmDialog
                    open={cancelling}
                    title="Cancel this TallyPrime setup?"
                    confirmLabel="Cancel the setup"
                    busy={busy}
                    onCancel={() => setCancelling(false)}
                    onConfirm={() => { setCancelling(false); void cancelSetup(); }}
                >
                    <p>The connector paired for this setup stops working, and nothing from it is kept. Your accounts, their history and any Google Sheet are not touched.</p>
                    <p className="mt-2">You can make a new pairing code straight away.</p>
                </ConfirmDialog>
            )}
        </>
    );
}
